// src/utils/tradeValidation.js
// Validates trade data for logical consistency
// Calculates expected P&L from entry/SL/TP/lot size and compares to logged P&L
// Catches errors and prevents manipulation

// ── PIP VALUES PER STANDARD LOT (1 lot = 100,000 units) ──────────
// Approximate USD pip values for 1 standard lot
const PIP_VALUES = {
  'EUR/USD': 10, 'GBP/USD': 10, 'AUD/USD': 10, 'NZD/USD': 10,
  'USD/JPY': 9.30,  'USD/CHF': 11.20, 'USD/CAD': 7.50,
  'EUR/JPY': 9.30,  'GBP/JPY': 9.30, 'AUD/JPY': 9.30,
  'EUR/GBP': 13.20, 'EUR/AUD': 6.80, 'EUR/CHF': 11.20,
  'GBP/AUD': 6.80,  'GBP/CHF': 11.20, 'GBP/CAD': 7.50,
  'XAU/USD': 10,    // 1 pip = $0.10 per oz, 100 oz per lot = $10/pip
  'XAG/USD': 50,    // 1 pip = $0.01 per oz, 5000 oz per lot
};

// Pip decimals — JPY pairs use 2 decimals, others use 4
const PIP_DECIMALS = {
  default:    0.0001,
  jpy:        0.01,
  'XAU/USD':  0.10,
  'XAG/USD':  0.001,
};

// ── FUTURES TICK VALUES ──────────────────────────────────────────
// Each contract — point value in USD
const FUTURES_SPECS = {
  'NQ':   { tickSize: 0.25, tickValue: 5.00,  pointValue: 20  }, // E-mini Nasdaq
  'MNQ':  { tickSize: 0.25, tickValue: 0.50,  pointValue: 2   }, // Micro Nasdaq
  'ES':   { tickSize: 0.25, tickValue: 12.50, pointValue: 50  }, // E-mini S&P
  'MES':  { tickSize: 0.25, tickValue: 1.25,  pointValue: 5   }, // Micro S&P
  'YM':   { tickSize: 1.0,  tickValue: 5.00,  pointValue: 5   }, // E-mini Dow
  'MYM':  { tickSize: 1.0,  tickValue: 0.50,  pointValue: 0.5 }, // Micro Dow
  'RTY':  { tickSize: 0.10, tickValue: 5.00,  pointValue: 50  }, // E-mini Russell
  'M2K':  { tickSize: 0.10, tickValue: 0.50,  pointValue: 5   }, // Micro Russell
  'GC':   { tickSize: 0.10, tickValue: 10.00, pointValue: 100 }, // Gold
  'MGC':  { tickSize: 0.10, tickValue: 1.00,  pointValue: 10  }, // Micro Gold
  'CL':   { tickSize: 0.01, tickValue: 10.00, pointValue: 1000}, // Crude Oil
};

const FUTURES_SYMBOLS = Object.keys(FUTURES_SPECS);

// ── CALCULATE EXPECTED P&L ───────────────────────────────────────
function calculateExpectedPnl(trade) {
  const { pair, direction, entryPrice, stopLoss, takeProfit, lotSize, contracts, result } = trade;
  if (!pair || !entryPrice || !direction) return null;

  // For Win, target is takeProfit. For Loss, target is stopLoss.
  let targetPrice;
  if      (result === 'Win')  targetPrice = takeProfit;
  else if (result === 'Loss') targetPrice = stopLoss;
  else                        return 0; // Breakeven

  if (!targetPrice) return null;

  // Calculate price movement (positive number)
  const movement = direction === 'Buy'
    ? targetPrice - entryPrice  // Buy: profit when price rises
    : entryPrice - targetPrice; // Sell: profit when price falls

  // Sign: Win = positive, Loss = negative (loss should be against direction)
  const sign = result === 'Win' ? 1 : -1;
  const expectedDirection = sign * Math.sign(movement);
  const absMovement = Math.abs(movement);

  // ── FUTURES ──
  if (FUTURES_SYMBOLS.includes(pair)) {
    const spec = FUTURES_SPECS[pair];
    const ct   = parseFloat(contracts || 0);
    if (!ct || !spec) return null;
    return expectedDirection * absMovement * spec.pointValue * ct;
  }

  // ── FOREX/COMMODITIES ──
  const lots = parseFloat(lotSize || 0);
  if (!lots) return null;

  const pipDecimal = pair === 'XAU/USD' ? PIP_DECIMALS['XAU/USD']
                  : pair === 'XAG/USD' ? PIP_DECIMALS['XAG/USD']
                  : pair.includes('JPY') ? PIP_DECIMALS.jpy
                  : PIP_DECIMALS.default;

  const pipValue = PIP_VALUES[pair];
  if (!pipValue) return null;

  const pips = absMovement / pipDecimal;
  return expectedDirection * pips * pipValue * lots;
}

// ── VALIDATE TRADE ──────────────────────────────────────────────
// Returns { valid: bool, warnings: [], errors: [], expectedPnl: number }
export function validateTradeLogic(trade) {
  const result = { valid: true, warnings: [], errors: [], expectedPnl: null };

  // Skip validation for Ongoing trades
  if (trade.result === 'Ongoing' || trade.status === 'open') return result;

  // Skip if missing critical data — can't validate without it
  const { pair, direction, entryPrice, stopLoss, takeProfit, lotSize, contracts, pnl, result: tradeResult } = trade;

  // Check direction consistency: Buy should have TP > entry > SL (price rises = win)
  // Sell should have SL > entry > TP (price falls = win)
  if (entryPrice && stopLoss && takeProfit && direction) {
    const e = parseFloat(entryPrice), s = parseFloat(stopLoss), t = parseFloat(takeProfit);
    if (direction === 'Buy') {
      if (s >= e) result.errors.push('For a Buy trade, Stop Loss must be below Entry Price.');
      if (t <= e) result.errors.push('For a Buy trade, Take Profit must be above Entry Price.');
    } else if (direction === 'Sell') {
      if (s <= e) result.errors.push('For a Sell trade, Stop Loss must be above Entry Price.');
      if (t >= e) result.errors.push('For a Sell trade, Take Profit must be below Entry Price.');
    }
  }

  // Try to calculate expected P&L
  const expected = calculateExpectedPnl(trade);
  result.expectedPnl = expected;

  // If we can calculate expected and have logged P&L — compare
  if (expected !== null && pnl !== undefined && pnl !== '' && pnl !== null) {
    const loggedPnl = parseFloat(pnl);

    // For Breakeven, expected is 0 — any P&L logged is suspicious
    if (tradeResult === 'Breakeven') {
      if (Math.abs(loggedPnl) > 5) {
        result.warnings.push(`Breakeven trade has $${loggedPnl.toFixed(2)} P&L. Breakeven should be near $0.`);
      }
      return result;
    }

    // Sign check: Win must have positive P&L, Loss must have negative
    if (tradeResult === 'Win' && loggedPnl <= 0) {
      result.errors.push(`Win trades must have positive P&L. You logged $${loggedPnl.toFixed(2)}.`);
    }
    if (tradeResult === 'Loss' && loggedPnl >= 0) {
      result.errors.push(`Loss trades must have negative P&L. You logged $${loggedPnl.toFixed(2)}.`);
    }

    // Magnitude check — logged P&L should be within reasonable range of expected
    // Allow 20% variance for spread, slippage, commissions, partial fills
    const tolerance = 0.20;
    const expectedAbs = Math.abs(expected);
    const loggedAbs   = Math.abs(loggedPnl);
    const minAcceptable = expectedAbs * (1 - tolerance);
    const maxAcceptable = expectedAbs * (1 + tolerance);

    if (loggedAbs < minAcceptable || loggedAbs > maxAcceptable) {
      const pctOff = expectedAbs > 0 ? Math.abs((loggedAbs - expectedAbs) / expectedAbs * 100) : 0;
      // Hard error if more than 100% off
      if (pctOff > 100) {
        result.errors.push(
          `P&L of $${loggedPnl.toFixed(2)} is far from the expected $${expected.toFixed(2)} based on your entry, ${tradeResult === 'Win' ? 'TP' : 'SL'}, and ${FUTURES_SYMBOLS.includes(pair) ? 'contracts' : 'lot size'}. Review your numbers.`
        );
      } else {
        // Warning for 20-100% off (could be slippage/commission)
        result.warnings.push(
          `P&L of $${loggedPnl.toFixed(2)} differs by ${pctOff.toFixed(0)}% from expected $${expected.toFixed(2)}. Check for typos in price, lot size, or commissions.`
        );
      }
    }
  }

  // RR sanity check
  if (trade.rrAchieved && entryPrice && stopLoss && takeProfit) {
    const e = parseFloat(entryPrice), s = parseFloat(stopLoss), t = parseFloat(takeProfit);
    const calculatedRR = Math.abs(e - s) > 0 ? Math.abs(t - e) / Math.abs(e - s) : 0;
    const loggedRR = parseFloat(trade.rrAchieved);
    if (calculatedRR > 0 && Math.abs(loggedRR - calculatedRR) / calculatedRR > 0.30 && loggedRR > 0.1) {
      result.warnings.push(
        `RR of ${loggedRR.toFixed(2)} differs from calculated ${calculatedRR.toFixed(2)} based on your entry, SL, and TP.`
      );
    }
  }

  if (result.errors.length > 0) result.valid = false;
  return result;
}

// Export for use in calculator/preview
export { calculateExpectedPnl, FUTURES_SYMBOLS, PIP_VALUES };
